import { useMemo, useRef, useState } from 'react';

import dotize from 'dotize';
import { useSelector } from 'react-redux';

import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import useDebounce from '@/hooks/useDebounce';
import {
  selectDraftCampaignName,
  selectDraftSubscribers,
} from '@/modules/campaigns/redux/selectors';
import { selectCampaignId } from '@/redux/session/selectors';

import { useCampaignSubscribers } from './useCampaignSubscribers';
import {
  buildErrorChips,
  createDynamicColumns,
  getSubscriberErrorChipKeys,
  getSubscriberName,
  Subscriber,
} from '../components/Tables/AudienceTable/columns';

interface UseAudiencePageOptions {
  ensureRequiredColumns?: boolean;
}

const rowMatchesQuery = (row: Record<string, unknown>, query: string): boolean =>
  Object.values(dotize.convert(row)).some(
    (value) => value != null && String(value).toLowerCase().includes(query)
  );

/** Shared logic for the Review Audience screen (outreach + payment-recovery). */
export const useAudiencePage = ({ ensureRequiredColumns = false }: UseAudiencePageOptions = {}) => {
  const [searchQuery, setSearchQuery] = useState('');
  // Debounced value throttles the server query; the draft filter stays on the
  // immediate value so client-side search feels instant.
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);

  const subscribers = useSelector(selectDraftSubscribers);
  const campaignName = useSelector(selectDraftCampaignName);
  const campaignId = useSelector(selectCampaignId);

  const shouldFetchFromAPI = !!campaignId && subscribers.length === 0;
  const isShowingReduxData = subscribers.length > 0;

  // The (trimmed) query the visible rows reflect: draft filters instantly on the
  // immediate value; saved data keys its server query off the debounced value.
  const activeQuery = (isShowingReduxData ? searchQuery : debouncedQuery).trim();

  const {
    subscribersData,
    fetchNextPage,
    hasNextPage: serverHasNextPage,
    totalRows,
    isFetchingNextPage,
    isSubscribersLoading,
  } = useCampaignSubscribers(shouldFetchFromAPI ? campaignId : undefined, {
    q: activeQuery || undefined,
  });

  const { currentPageIndex, setCurrentPageIndex, goToNextPage, hasNextPage, currentPageData } =
    useServerPagination(subscribersData?.pages || [], fetchNextPage, {
      dataSelector: (page) => page?.subscribers || [],
      resetDependencies: [activeQuery],
      externalIsFetching: isFetchingNextPage,
      serverHasNextPage,
    });

  // Unfiltered set for page-level checks (error count, required fields).
  const displayData = isShowingReduxData ? subscribers : currentPageData;

  // Draft columns come from the full unfiltered set, so client-side search never
  // changes them; saved data derives them from the current (server-searched) page.
  const columns = useMemo(
    () =>
      createDynamicColumns(displayData as unknown as Subscriber[], isShowingReduxData, {
        ensureRequiredColumns,
      }),
    [displayData, isShowingReduxData, ensureRequiredColumns]
  );

  // Client-side search over draft data (DataTable has no global filter).
  // Saved data searches server-side via the `q` param above.
  const draftData = useMemo(() => {
    if (!activeQuery) {
      return subscribers;
    }
    const query = activeQuery.toLowerCase();
    return subscribers.filter((row) => rowMatchesQuery(row as Record<string, unknown>, query));
  }, [subscribers, activeQuery]);

  const data = (isShowingReduxData ? draftData : currentPageData) as unknown as Subscriber[];

  // Per-campaign key so dynamic columns don't bleed across campaigns.
  const {
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
  } = useColumnVisibility(columns, `audienceColumnVisibility-${campaignId ?? 'draft'}`);

  const isLoading = isSubscribersLoading && currentPageData.length === 0 && !isShowingReduxData;
  // 0 rows means no-match vs no-data, in sync with whichever query drives the mode.
  const hasSearch = !!activeQuery;

  const rowCount = isShowingReduxData ? draftData.length : (totalRows ?? currentPageData.length);

  return {
    campaignId,
    campaignName,
    isShowingReduxData,
    displayData,
    // Table
    columns,
    data,
    rowCount,
    // Column visibility
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
    isCustomizeColumnsOpen,
    setIsCustomizeColumnsOpen,
    customizeButtonRef,
    // Search
    searchQuery,
    handleSearchChange: setSearchQuery,
    hasSearch,
    // Server pagination
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    isFetchingNextPage,
    // Status
    isLoading,
  };
};

export type UseAudiencePageReturn = ReturnType<typeof useAudiencePage>;

// Error warnings + "View" filter for Review Audience (both pages). The filter is
// derived, so resolving every error can never strand the table on zero rows.
const EMPTY_DISMISSED: string[] = [];

// The warnings detail line only shows the first few names — cap the work and the DOM node.
const MAX_ERROR_NAMES = 50;

export const useErrorChipsFilter = (page: UseAudiencePageReturn) => {
  const errorSubscribers = useMemo(
    () =>
      page.isShowingReduxData ? (page.displayData as Subscriber[]).filter((s) => s.error) : [],
    [page.displayData, page.isShowingReduxData]
  );
  const errorCount = errorSubscribers.length;
  const errorNames = useMemo(() => {
    const names = errorSubscribers
      .slice(0, MAX_ERROR_NAMES)
      .map(getSubscriberName)
      .filter(Boolean)
      .join(', ');
    const overflow = errorCount - MAX_ERROR_NAMES;
    return overflow > 0 ? `${names} +${overflow}` : names;
  }, [errorSubscribers, errorCount]);
  const errorChips = useMemo(() => buildErrorChips(errorSubscribers), [errorSubscribers]);

  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [dismissedColumns, setDismissedColumns] = useState<string[]>(EMPTY_DISMISSED);

  const visibleChips = useMemo(
    () => errorChips.filter((chip) => !dismissedColumns.includes(chip.key)),
    [errorChips, dismissedColumns]
  );
  const hasVisibleChips = visibleChips.length > 0;

  // Render-time reset when the last visible chip goes — every error resolved, or the
  // remaining chips dismissed. Keying on visibleChips (not errorCount) stops a chip
  // dismissed while its siblings later resolve from latching the filter and silently
  // re-filtering when a new error lands on another column.
  const [prevHadVisibleChips, setPrevHadVisibleChips] = useState(hasVisibleChips);
  if (prevHadVisibleChips !== hasVisibleChips) {
    setPrevHadVisibleChips(hasVisibleChips);
    if (!hasVisibleChips) {
      setShowErrorsOnly(false);
      setDismissedColumns(EMPTY_DISMISSED);
    }
  }

  // Inert when nothing errors or every live chip is dismissed → full table.
  const errorFilterActive = showErrorsOnly && hasVisibleChips;

  const activeQuery = (page.searchQuery ?? '').trim().toLowerCase();
  // Rows still erroring on a visible chip's column, intersected with the live search.
  const filteredErrorSubscribers = useMemo(() => {
    if (!errorFilterActive) {
      return [];
    }
    const visibleChipKeys = new Set(visibleChips.map((chip) => chip.key));
    return errorSubscribers.filter(
      (sub) =>
        getSubscriberErrorChipKeys(sub).some((key) => visibleChipKeys.has(key)) &&
        (!activeQuery || rowMatchesQuery(sub as Record<string, unknown>, activeQuery))
    );
  }, [errorFilterActive, errorSubscribers, visibleChips, activeQuery]);

  const tableData = errorFilterActive ? filteredErrorSubscribers : page.data;
  const tableRowCount = errorFilterActive ? filteredErrorSubscribers.length : page.rowCount;

  const showErrors = () => {
    setShowErrorsOnly(true);
    setDismissedColumns(EMPTY_DISMISSED);
  };

  // Per-chip dismissal; removing the last chip clears the whole filter.
  const removeChip = (key: string) => {
    if (visibleChips.length <= 1) {
      setShowErrorsOnly(false);
      setDismissedColumns(EMPTY_DISMISSED);
    } else {
      setDismissedColumns((prev) => [...prev, key]);
    }
  };

  return {
    errorCount,
    errorNames,
    errorFilterActive,
    visibleChips,
    tableData,
    tableRowCount,
    showErrors,
    removeChip,
  };
};
